import api from './api';

export interface IWOperador {
  id: number;
  user_id: string;
  nome_empresa: string;
  descricao?: string;
  ilha_id?: number;
  ilha_nome?: string;
  email_contacto?: string;
  telefone?: string;
  iban?: string;
  taxa_comissao: number;
  verificado: number;
  status: 'PENDING' | 'ACTIVE' | 'SUSPENDED';
  total_experiencias?: number;
  created_at: string;
}

export interface IWExperiencia {
  id: number;
  titulo: string;
  descricao?: string;
  descricao_curta?: string;
  slug: string;
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ARCHIVED';
  preco_adulto: number;
  preco_crianca?: number;
  duracao_minutos?: number;
  capacidade_minima?: number;
  capacidade_maxima?: number;
  ponto_encontro?: string;
  politica_cancelamento: string;
  horas_cancelamento: number;
  destaque: number;
  ilha_id: number;
  ilha_nome: string;
  categoria_id: number;
  categoria_nome: string;
  nome_empresa: string;
  media_avaliacoes: number;
  total_avaliacoes: number;
  media?: { id: number; url: string; capa: number }[];
  created_at: string;
}

export interface IWReserva {
  id: number;
  referencia: string;
  num_adultos: number;
  num_criancas: number;
  total_cvescudos: number;
  metodo_pagamento: string;
  pagamento_status: string;
  status: string;
  created_at: string;
  exp_titulo: string;
  nome_empresa: string;
  data_exp: string;
  hora_exp: string;
}

export interface IWDashboard {
  experiencias: { total: number; activas: number };
  operadores: { total: number; activos: number; verificados: number };
  reservas: { total: number; pagas: number; receita: number };
  avaliacoes: { media: number; total: number };
}

export interface IWHeroSlide {
  id: number;
  ilha: string;
  titulo: string;
  subtitulo?: string;
  imagem_url: string;
  /** Link YouTube opcional — se presente, o vídeo substitui a imagem no hero */
  video_url?: string;
  ordem: number;
  activo: number;
}

export interface IWGuia {
  id: number;
  nome: string;
  foto_url?: string;
  telefone?: string;
  email?: string;
  whatsapp?: string;
  bio?: string;
  ilha_id?: number;
  ilha_nome?: string;
  idiomas?: string[];
  areas_atuacao?: string;
  tipo_experiencias?: string;
  certificacoes?: string;
  anos_experiencia?: number;
  nif?: string;
  dados_pagamento?: string;
  status: 'ACTIVE' | 'INACTIVE';
  total_experiencias?: number;
  experiencia_ids?: number[];
}

const iwalkcvService = {
  // Dashboard
  getDashboard: async (): Promise<IWDashboard> => {
    const res = await api.get('/iwalkcv/admin/dashboard');
    return res.data.data;
  },

  // Operadores
  getOperadores: async (params?: { status?: string; page?: number; limit?: number }) => {
    const clean: Record<string, string> = {};
    if (params?.status) clean.status = params.status;
    if (params?.page) clean.page = String(params.page);
    if (params?.limit) clean.limit = String(params.limit);
    const q = new URLSearchParams(clean).toString();
    const res = await api.get(`/iwalkcv/admin/operadores${q ? `?${q}` : ''}`);
    return res.data;
  },
  searchUsers: async (q: string): Promise<{ uid: string; email: string; nome: string; telefone: string }[]> => {
    if (q.length < 2) return [];
    const res = await api.get(`/iwalkcv/admin/users/search?q=${encodeURIComponent(q)}`);
    return res.data.data || [];
  },
  createOperador: async (data: Partial<IWOperador>) => {
    const res = await api.post('/iwalkcv/admin/operadores', data);
    return res.data;
  },
  deleteOperador: async (id: number) => {
    const res = await api.delete(`/iwalkcv/admin/operadores/${id}`);
    return res.data;
  },
  getOperador: async (id: number) => {
    const res = await api.get(`/iwalkcv/admin/operadores/${id}`);
    return res.data;
  },
  updateOperador: async (id: number, data: Partial<IWOperador>) => {
    const res = await api.put(`/iwalkcv/admin/operadores/${id}`, data);
    return res.data;
  },
  verificarOperador: async (id: number) => {
    const res = await api.put(`/iwalkcv/admin/operadores/${id}/verificar`);
    return res.data;
  },
  setOperadorStatus: async (id: number, status: string) => {
    const res = await api.put(`/iwalkcv/admin/operadores/${id}/status`, { status });
    return res.data;
  },

  // Experiências
  getExperiencias: async (params?: { status?: string; search?: string; page?: number; limit?: number }) => {
    const q = new URLSearchParams(Object.fromEntries(Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== '')) as any).toString();
    const res = await api.get(`/iwalkcv/admin/experiencias${q ? `?${q}` : ''}`);
    return res.data;
  },
  getExperiencia: async (id: number) => {
    const res = await api.get(`/iwalkcv/admin/experiencias/${id}`);
    return res.data;
  },
  updateExperiencia: async (id: number, data: Partial<IWExperiencia> & { imagem_capa_url?: string }) => {
    const res = await api.put(`/iwalkcv/admin/experiencias/${id}`, data);
    return res.data;
  },
  setExperienciaStatus: async (id: number, status: string) => {
    const res = await api.put(`/iwalkcv/admin/experiencias/${id}/status`, { status });
    return res.data;
  },
  createExperiencia: async (data: any) => {
    const res = await api.post('/iwalkcv/admin/experiencias', data);
    return res.data;
  },
  deleteExperiencia: async (id: number) => {
    const res = await api.delete(`/iwalkcv/admin/experiencias/${id}`);
    return res.data;
  },

  // Reservas
  getReservas: async (params?: { page?: number; limit?: number; status?: string; pagamento_status?: string; search?: string }) => {
    const q = new URLSearchParams(Object.fromEntries(Object.entries(params || {}).filter(([,v]) => v !== undefined && v !== '')) as any).toString();
    const res = await api.get(`/iwalkcv/admin/reservas${q ? `?${q}` : ''}`);
    return res.data;
  },
  updateReservaStatus: async (id: number, data: { status?: string; pagamento_status?: string }) => {
    const res = await api.put(`/iwalkcv/admin/reservas/${id}/status`, data);
    return res.data;
  },

  // CMS — Hero Slides
  getHeroSlides: async () => {
    const res = await api.get('/iwalkcv/admin/cms/hero-slides');
    return res.data;
  },
  createHeroSlide: async (data: Omit<IWHeroSlide, 'id'>) => {
    const res = await api.post('/iwalkcv/admin/cms/hero-slides', data);
    return res.data;
  },
  updateHeroSlide: async (id: number, data: Partial<IWHeroSlide>) => {
    const res = await api.put(`/iwalkcv/admin/cms/hero-slides/${id}`, data);
    return res.data;
  },
  deleteHeroSlide: async (id: number) => {
    const res = await api.delete(`/iwalkcv/admin/cms/hero-slides/${id}`);
    return res.data;
  },
  uploadHeroImage: async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post('/iwalkcv/admin/cms/hero-slides/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data.data.url;
  },

  // Ilhas
  getIlhas: async () => { const res = await api.get('/iwalkcv/admin/ilhas'); return res.data; },
  createIlha: async (data: any) => { const res = await api.post('/iwalkcv/admin/ilhas', data); return res.data; },
  updateIlha: async (id: number, data: any) => { const res = await api.put(`/iwalkcv/admin/ilhas/${id}`, data); return res.data; },
  deleteIlha: async (id: number) => { const res = await api.delete(`/iwalkcv/admin/ilhas/${id}`); return res.data; },
  toggleIlhaActivo: async (id: number, activo: number) => { const res = await api.put(`/iwalkcv/admin/ilhas/${id}`, { activo }); return res.data; },

  // Categorias
  getCategorias: async () => { const res = await api.get('/iwalkcv/admin/categorias'); return res.data; },
  createCategoria: async (data: any) => { const res = await api.post('/iwalkcv/admin/categorias', data); return res.data; },
  updateCategoria: async (id: number, data: any) => { const res = await api.put(`/iwalkcv/admin/categorias/${id}`, data); return res.data; },
  deleteCategoria: async (id: number) => { const res = await api.delete(`/iwalkcv/admin/categorias/${id}`); return res.data; },

  // Avaliações
  getAvaliacoes: async (params?: { status?: string; page?: number; limit?: number }) => {
    const q = new URLSearchParams(Object.fromEntries(Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== '')) as any).toString();
    const res = await api.get(`/iwalkcv/admin/avaliacoes${q ? '?' + q : ''}`);
    return res.data;
  },
  setAvaliacaoStatus: async (id: number, status: string) => { const res = await api.put(`/iwalkcv/admin/avaliacoes/${id}/status`, { status }); return res.data; },

  // Disponibilidades
  getDisponibilidades: async (params?: { experiencia_id?: number; page?: number; limit?: number }) => {
    const q = new URLSearchParams(Object.fromEntries(Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null)) as any).toString();
    const res = await api.get(`/iwalkcv/admin/disponibilidade${q ? '?' + q : ''}`);
    return res.data;
  },
  deleteDisponibilidade: async (id: number) => { const res = await api.delete(`/iwalkcv/admin/disponibilidade/${id}`); return res.data; },

  // Guias
  getGuias: async (search?: string) => { const res = await api.get(`/iwalkcv/admin/guias${search ? `?search=${encodeURIComponent(search)}` : ''}`); return res.data; },
  getGuia: async (id: number) => { const res = await api.get(`/iwalkcv/admin/guias/${id}`); return res.data; },
  createGuia: async (data: any) => { const res = await api.post('/iwalkcv/admin/guias', data); return res.data; },
  updateGuia: async (id: number, data: any) => { const res = await api.put(`/iwalkcv/admin/guias/${id}`, data); return res.data; },
  deleteGuia: async (id: number) => { const res = await api.delete(`/iwalkcv/admin/guias/${id}`); return res.data; },
};

export default iwalkcvService;
